<template>
  <main class="container p-4 pt-2 text-sp-primary">
    <Search />
    <section class="container p-0 my-8 mt-0 text-sp-primary">
      <div v-if="loading" class="fade"><Loading /></div>
      <div v-else-if="error">{{ $t("error") }}</div>
      <div v-else>
        <div class="flex justify-center mb-4">
          <h1 class="heading inline-block text-2xl text-sp-accent text-center font-bold">
            {{ articleName || "Untitled Article" }}
          </h1>
        </div>
        <div ref="articleContainer" class='article' v-html="articleBody || '<p>No content available</p>'"></div>
      </div>
    </section>
  </main>
</template>


<script>
import { ref, computed, onMounted, watch } from "vue"
import { useRoute } from 'vue-router'
import { getUserData } from '../modules/collectData.js'
import { useHelpCenterStore } from '../stores/helpCenter'
import { useHelpCenterFetch } from '../composables/useHelpCenterFetch'
import { useArticleContent } from '../composables/useArticleContent'
import Search from '../components/Search.vue'
import Loading from '../components/Loading.vue'

export default {
  components: { Search, Loading },
  setup() {
    const { lang } = getUserData()
    const route = useRoute()
    const helpCenter = useHelpCenterStore()

    const articleContainer = ref(null)

    const { data, loading, error, load } = useHelpCenterFetch(
      () => `helpcenter/${lang}/articles/${route.params.articleId}`
    )

    const articleName = computed(() => data.value?.title || null)
    const articleBody = computed(() => data.value?.body || null)

    const linkContext = computed(() => ({
      categoryId: data.value?.categoryId ?? route.params.categoryId,
      sectionId: data.value?.sectionId ?? route.params.sectionId
    }))

    useArticleContent(articleContainer, articleBody, { linkContext })

    const persistStoreState = (articleIdParam) => {
      const answer = data.value
      if (!answer) return

      if (answer.title) {
        helpCenter.setCurrentArticle({
          id: articleIdParam,
          name: answer.title
        })
      }

      if (answer.sectionName && answer.sectionId) {
        helpCenter.setCurrentSection({
          id: answer.sectionId,
          name: answer.sectionName
        })
      }

      if (answer.categoryName && answer.categoryId) {
        helpCenter.setCurrentCategory({
          id: answer.categoryId,
          name: answer.categoryName
        })
      }
    }

    const reload = async () => {
      const articleIdParam = route.params.articleId
      await load()
      persistStoreState(articleIdParam)
    }

    onMounted(reload)

    watch(
      () => route.params.articleId,
      (newId) => {
        if (newId) reload()
      }
    )

    return {
      loading,
      error,
      articleName,
      articleBody,
      articleContainer
    }
  }
}
</script>



<style>
.article {
  overflow: hidden;         
  white-space: normal;    
  word-wrap: break-word;    
  word-break: break-word;    
  text-overflow: ellipsis; 
}

.article p {
  margin: 1rem 0;
}

.article a {
  text-decoration: underline;
  transition: 150ms ease-in-out;
}
.article a:hover {
  color: var(--sp-accent);
}

.article h1,
.article h2,
.article h3 {
  text-align: center;
  margin: 1.5rem 0;
  font-weight: bold;
  font-size: 1.25rem;
  color: var(--sp-accent);
  text-decoration: none;
}
.article h1 a,
.article h2 a,
.article h3 a { 
  text-decoration: none;
}

.article ul, 
.article ol {
  margin-left: 1.5rem;
}
.article ul {
  list-style: none; 
}
.article ol {
  list-style-type: auto;
}
.article ul li {
  position: relative;
  padding-left: 1.5em;
}
.article ul li:not([style*="list-style-type: none"]):not([style*="list-style-type:none"])::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.2em;
  width: 1em;
  height: 1em;
  background-image: url('@/assets/checkbox-done.png');
  background-size: contain;
  background-repeat: no-repeat;
}

.article hr {
  padding: 0;
  margin: 1.5rem 0;
  border: 0;
  border: none;
  height: 1px;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0) 100%
  );
}

.image-with-lightbox,
.wysiwyg-image {
  pointer-events: none !important;
  display: inline-block;
  min-width: 32px;
  max-width: 500px;
}
.article img {
  border-radius: 6px;
  max-width: 500px;
  display: inline-block;
  filter: blur(10px);
  opacity: 0;
  transition: filter 0.3s ease-out, opacity 0.3s ease-out;
}
.article img.is-loading {
  background: linear-gradient(
    90deg,
    #1f2937 25%, 
    #111827 37%, 
    #1f2937 63%
  );

  background-size: 400% 100%;
  animation: shimmer 1.2s infinite;

  filter: blur(10px);
  opacity: 0.7;
}
.article img.lazy-loaded {
  filter: blur(0);
  opacity: 1;
  animation: none;
  background: transparent;
}
@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}

.note {
  position: relative;
  padding: 0.5rem;
  padding-left: 10px;
  margin: 1rem 0.75rem;
  border-left: 2px solid transparent;
  background: rgba(66, 89, 128, 0.4);
}
.note p:first-child {
  margin-top: 0;
}
.note p:last-child {
  margin-bottom: 0;
}
.note-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.note-default {
  border-color: #818a91;
}
.note-info, .note--info {
  border-color: #925a29;
}
.note-warning, .note--warning {
  border-color: #925a29;
}
.note-success, .note--success {
  border-color: #925a29;
}
.note-danger, .note--danger {
  border-color: #925a29;
}

.accordion {
  padding-top: 0;
  margin-bottom: 0.5rem;
  border-radius: 0.5rem;
}
.accordion__item-title {
  cursor: pointer;
  position: relative;
  font-size: 1.125rem;
  padding: 8px 8px 8px 38px;
  background: transparent;
  border: thin solid rgba(255, 255, 255, 0.2);
  margin-top: 10px;
  color: var(--sp-accent);
  user-select: none;
  -webkit-user-select: none; 
  -moz-user-select: none; 
  -ms-user-select: none; 
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
}
.accordion__item-title > strong {
  display: block;
  margin-left: 8px;
}
.accordion__item-title > span.wysiwyg-color-black > strong {
  display: block;
  margin-left: 4px;
}
.accordion__item-title > p.wysiwyg-text-align-left {
  margin-bottom: 0;
}
.accordion__item-title > p {
  margin-bottom: 0;
}
.accordion__item-title > p > span.wysiwyg-color-black {
  margin-bottom: 0;
}
.accordion__item-title:not(.accordion__item-title--active) {
  border-radius: 6px;
  margin-top: 10px;
}
.accordion__item-title p:first-of-type,
.accordion__item-title--active p:first-of-type {
  margin-top: 6px;
}
.accordion__item-title:before {
  position: absolute;
  content: "";
}
.accordion__item-content {
  display: none; 
  padding: 0.5rem 1rem 1rem 1rem;
  border: thin solid rgba(255, 255, 255, 0.2);
  border-top: none;
  margin-bottom: 0.5rem;
	border-bottom-left-radius: 6px;
	border-bottom-right-radius: 6px;
  transition: max-height 10.3s ease; 
  overflow: hidden;
}
.accordion__item-content p:last-child {
  margin-bottom: 0;
}
.accordion--default .accordion__item-title:before {
  content: "";
  position: absolute;
  background-image: url('@/assets/icon-arrow-btn.png');
  background-size: contain;
  background-repeat: no-repeat;
  width: 20px;
  height: 20px;
  top: 50%;
  left: 15px;
  transform: translateY(-50%) rotate(90deg);
  transform-origin: center;
  transform-origin: center center;
}
.accordion__item-title--active,
.accordion--default .accordion__item-title--active {
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  margin-top: 10px;
  border-bottom: none;
}
.accordion--default .accordion__item-title--active:before {
  transition: transform 0.3s;
  transform: translateY(-50%) rotate(180deg);
}
.accordion--colored .accordion__item-title:before,
.accordion--colored .accordion__item-title:after {
  top: 50%;
}
.accordion--colored .accordion__item-title:before {
  width: 10px;
  height: 2px;
  margin-top: -1px;
  background-color: #818a91;
}
.accordion--colored .accordion__item-title:before {
  left: 20px;
}
.accordion--colored .accordion__item-title:after {
  position: absolute;
  width: 2px;
  height: 10px;
  content: "";
}
.accordion--colored .accordion__item-title:after {
  margin-top: -5px;
  background-color: #818a91;
  left: 24px;
}
.accordion__item-title--active {
  color: var(--sp-accent);
}
.accordion--colored .accordion__item-title--active:before {
  background-color: #fff;
}
.accordion--colored .accordion__item-title--active:after {
  display: none;
}
.accordion__item-content--visible {
  display: block; 
}

.tab,
.tab.is-hidden {
  display: none;
}
.tab:not(.is-hidden) {
  display: block;
}
.tabs {
  margin-bottom: 0.5rem;
}
.tab {
  margin-top: -1px;
  border: 1px solid transparent;
  border-bottom-right-radius: 6px;
  border-bottom-left-radius: 6px;
}
.tab p:last-child {
  margin-bottom: 0;
}
.tab:nth-child(2) {
  border-top-left-radius: 0;
}
.tabs-link {
  display: block;
  font-weight: 600;
  padding: 8px 30px;
  margin: 0 0 5px 0;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-top-color: rgba(255, 255, 255, 0.2);
  border-right-color: rgba(255, 255, 255, 0.2);
  border-left-color: rgba(255, 255, 255, 0.2);
  border-bottom-color: transparent;
  background: rgba(21, 28, 40, 0.7);
  user-select: none;
  -webkit-user-select: none; 
  -moz-user-select: none; 
  -ms-user-select: none; 
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
}
.tabs-link.is-active {
  cursor: pointer;
  border-color: rgba(255, 255, 255, 0.2);;
  color: var(--sp-accent);
  background: linear-gradient(
    to bottom,
    rgba(42, 66, 118, 0.7) 0%, 
    rgba(42, 66, 118, 0.7) 60%,  
    rgba(42, 66, 118, 0) 100%
  );
  border-bottom: 0;
}
.tabs--colored-1 .tabs-link.is-active {
  color: #fffded;
  background: linear-gradient(
    to bottom,
    rgba(42, 66, 118, 0.7) 0%, 
    rgba(42, 66, 118, 0.7) 60%,  
    rgba(42, 66, 118, 0) 100%
  );
  border-color: rgba(255, 255, 255, 0.5);
  border-bottom-color: transparent; 
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.tabs--colored-2 .tabs-link.is-active {
  color: #fffded;
  background: linear-gradient(
    to bottom,
    rgba(42, 66, 118, 0.7) 0%, 
    rgba(42, 66, 118, 0.7) 60%,  
    rgba(42, 66, 118, 0) 100%
  );
  border-color: rgba(255, 255, 255, 0.5);
  border-bottom-color: transparent;
  border-radius: 6px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.tabs-menu {
  margin-bottom: 10px;
}
.tabs-menu,
.tabs-menu span {
  display: flex;
  flex-wrap: wrap;
  gap: 0 0.4rem;
}

.list-bullet {
  list-style: none;
  list-style-type: none !important;
  position: relative; 
}
.list-bullet > li {
  position: relative;
}
.list-bullet > li:before {
  position: absolute;
  top: -2px;
  content: '\eb81';
  font-family: "remixicon";
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  color: white;
}
.list-colored > li:before {
  left: 0;
}
.list-colored > li:before {
  text-align: center;
  background-color: rgba(64, 13, 89, 1);
  border-radius: 50%;
}
.list-colored > li:before {
  position: absolute;
  top: -3px;
  z-index: 1;
  width: 30px;
  height: 30px;
  font-size: calc(18px* 0.85);
  line-height: 30px;
  color: #fff;
  content: counter(list);
}
.list-colored > li {
  position: relative;
  counter-increment: list;
}
.article .list-bullet, 
.article .list-colored {
  list-style-type: none;
  counter-reset: list;
  margin-left: 0;
  padding-left: 1rem;
}

.article table {
  border: thin solid rgba(255, 255, 255, 0.2);
  border-collapse: separate !important;
  border-spacing: 0;
  border-radius: 6px;
  overflow: hidden;
  margin: 1rem 0;
}
.article table td, 
.article table th {
  padding: 0.5rem 1rem;
}
.article table td:last-of-type {
  padding-bottom: 1rem;
} 
.article table img {
  max-width: 99%;
}
.article table th {
  background-color: transparent !important;
}
.article figure {
  width: 100% !important;
  height: 100% !important;
}
.article figure th,
.article figure td,
.article figure tr {
  border: thin solid rgba(255, 255, 255, 0.2);
}
</style>